import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { PlugZap, Unplug } from 'lucide-react'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

export default function SquareConnectionCard({ connection, onConnectionChange }) {
  const [connecting, setConnecting] = useState(false)

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const { data, error } = await supabase.functions.invoke('square-market-sync', {
        body: { action: 'authorize' }
      })

      if (error) throw error

      // Open Square OAuth page in new window
      window.open(data.authorization_url, '_blank', 'width=600,height=700')
      toast.info('Complete the Square authorization in the new window')
    } catch (err) {
      toast.error('Failed to start Square connection', { description: err.message })
    } finally {
      setConnecting(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      await supabase
        .from('square_sync_config')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', connection.id)

      toast.success('Square disconnected')
      onConnectionChange()
    } catch (err) {
      toast.error('Failed to disconnect', { description: err.message })
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Square POS</CardTitle>
            <CardDescription>
              Connect Square to automatically pull market sales data
            </CardDescription>
          </div>
          {connection ? (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Connected</Badge>
          ) : (
            <Badge variant="outline">Not connected</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {connection ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Location</span>
                <p className="font-medium">{connection.location_name || connection.location_id}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Connected</span>
                <p className="font-medium">
                  {new Date(connection.created_at).toLocaleDateString('en-GB')}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleDisconnect}>
              <Unplug className="h-4 w-4 mr-2" />
              Disconnect
            </Button>
          </div>
        ) : (
          <Button onClick={handleConnect} disabled={connecting}>
            <PlugZap className="h-4 w-4 mr-2" />
            {connecting ? 'Connecting...' : 'Connect Square'}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}
